import React from 'react';

import './Badge.css';

export type BadgeVariant = 'complete' | 'current' | 'action-needed' | 'returned' | 'rejected' | 'cancelled' | 'denied';

interface BadgeProps {
  /**
   * The variant of the badge
   */
  variant: BadgeVariant;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

/**
 * Badge component
 *
 * Displays a status badge with different variants for application status tracking.
 *
 * @example
 * <Badge variant="complete" />
 * <Badge variant="current" />
 * <Badge variant="action-needed" />
 * <Badge variant="returned" />
 */
export function Badge({ variant, className = '', 'data-testid': dataTestId }: BadgeProps) {
  const baseTestId = dataTestId ?? `badge-${variant}`;
  const getBadgeText = () => {
    switch (variant) {
      case 'complete':
        return 'Complete';
      case 'current':
        return 'Current Step';
      case 'action-needed':
        return 'Action Needed';
      case 'returned':
        return 'Returned for Updates';
      case 'rejected':
        return 'Declined';
      case 'cancelled':
        return 'Cancelled';
      case 'denied':
        return 'Access Denied';
      default:
        return '';
    }
  };

  return (
    <span
      aria-label={`Status: ${getBadgeText()}`}
      className={`badge badge--${variant} ${className}`}
      data-testid={baseTestId}
      role='status'>
      <span
        aria-hidden='true'
        className='badge__text'
        data-testid={`${baseTestId}-text`}>
        {getBadgeText()}
      </span>
    </span>
  );
}

export default Badge;
